import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Grid3x3, AlertTriangle } from 'lucide-react';
import { diagnosticsAPI, entitiesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * A&L Managed Booking Matrix (Phase A inspection tooling).
 *
 * Cross-tab of (property.management_type x booking.management_type x
 * payment_state) with count + amount per bucket, reusing the live
 * booking_payment_state() predicate. Highlights the bucket the 2026-07-18
 * restamp cannot see by construction: property flagged managed, booking
 * stamp lagging, still landing as a direct receivable.
 */
function fmt(n) {
  if (n === null || n === undefined) return '-';
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function AlManagedBookingMatrixPanel() {
  var [entities, setEntities] = useState([]);
  var [selectedId, setSelectedId] = useState('');
  var [loading, setLoading] = useState(false);
  var [result, setResult] = useState(null);

  useEffect(function () {
    entitiesAPI.getAll().then(function (res) {
      var list = res.data || [];
      list.sort(function (a, b) { return (a.name || '').localeCompare(b.name || ''); });
      setEntities(list);
    }).catch(function () {});
  }, []);

  var run = async function () {
    if (!selectedId) return;
    setLoading(true);
    setResult(null);
    try {
      var res = await diagnosticsAPI.alManagedBookingMatrix(selectedId);
      setResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var mismatchTotal = result?.property_managed_booking_not_stamped_direct_total_native;
  var hasMismatch = mismatchTotal !== undefined && mismatchTotal !== null && Number(mismatchTotal) !== 0;

  return (
    <Card data-testid="al-managed-booking-matrix-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Grid3x3 className="h-4 w-4" />
          A&amp;L Managed Booking Matrix
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Cross-tab of property vs booking management_type vs payment state, with
          amounts. The highlighted bucket below is the population the management-type
          restamp cannot see by construction (its candidate walk only starts from
          properties already flagged managed).
        </p>
        <div className="flex items-center gap-2">
          <select
            className="text-sm border rounded px-2 py-1.5 bg-background flex-1"
            value={selectedId}
            onChange={function (e) { setSelectedId(e.target.value); }}
          >
            <option value="">Select entity...</option>
            {entities.map(function (ent) {
              return <option key={ent.id} value={ent.id}>{ent.name}</option>;
            })}
          </select>
          <Button size="sm" onClick={run} disabled={loading || !selectedId}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run'}
          </Button>
        </div>

        {result && (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{result.entity_name}</span>
              <Badge variant="outline" className="font-mono text-xs">{result.entity_id}</Badge>
            </div>

            {result.note && result.buckets.length === 0 ? (
              <div className="bg-muted/50 rounded p-3 text-xs text-muted-foreground">{result.note}</div>
            ) : (
              <>
                <div className="bg-muted/50 rounded p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">Direct receivable total (Unpaid bookings)</span>
                    <span className="font-mono font-semibold">{fmt(result.direct_receivable_total_native)} {result.buckets[0]?.currency}</span>
                  </div>
                </div>

                {result.scope_excluded_count > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">Pre-2026 unpaid bookings (scope-excluded, outside total)</span>
                      <span className="font-mono font-semibold">
                        {result.scope_excluded_count} / {fmt(result.scope_excluded_total_native)} {result.buckets[0]?.currency}
                      </span>
                    </div>
                    {result.scope_excluded_note && (
                      <p className="text-xs text-muted-foreground mt-1">{result.scope_excluded_note}</p>
                    )}
                  </div>
                )}

                <div className={
                  'rounded p-3 border ' +
                  (hasMismatch
                    ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900'
                    : 'bg-muted/50 border-transparent')
                }>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold flex items-center gap-1">
                      {hasMismatch && <AlertTriangle className="h-3.5 w-3.5 text-red-600" />}
                      Property managed, booking not stamped, still direct
                    </span>
                    <span className="font-mono font-semibold">{fmt(mismatchTotal)} {result.buckets[0]?.currency}</span>
                  </div>
                </div>

                {/* Bucket cross-tab */}
                <div className="overflow-x-auto">
                  <table className="text-xs w-full border-collapse">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2">property.management_type</th>
                        <th className="py-1 pr-2">booking.management_type</th>
                        <th className="py-1 pr-2">payment_state</th>
                        <th className="py-1 pr-2 text-right">count</th>
                        <th className="py-1 pr-2 text-right">amount</th>
                        <th className="py-1 pr-2">direct?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.buckets.map(function (b, i) {
                        var flagged = b.property_management_type === 'managed'
                          && b.booking_management_type !== 'managed'
                          && b.counts_toward_direct_receivable;
                        return (
                          <tr key={i} className={'border-b border-border/30' + (flagged ? ' bg-amber-50 dark:bg-amber-950/30' : '')}>
                            <td className="py-1 pr-2 font-mono">{b.property_management_type}</td>
                            <td className="py-1 pr-2 font-mono">{b.booking_management_type}</td>
                            <td className="py-1 pr-2 font-mono">{b.payment_state}</td>
                            <td className="py-1 pr-2 font-mono text-right">{b.count}</td>
                            <td className="py-1 pr-2 font-mono text-right">{fmt(b.amount_native)} {b.currency}</td>
                            <td className="py-1 pr-2">
                              {b.counts_toward_direct_receivable
                                ? <Badge variant={flagged ? 'destructive' : 'default'} className="text-[10px]">yes</Badge>
                                : <Badge variant="outline" className="text-[10px] text-muted-foreground">no</Badge>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Receivable age histogram, by check_out year */}
                {result.receivable_age_histogram && result.receivable_age_histogram.length > 0 && (
                  <div>
                    <h5 className="font-semibold text-xs mb-1">Direct-receivable age (by check_out year)</h5>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full border-collapse">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="py-1 pr-2">checkout_year</th>
                            <th className="py-1 pr-2 text-right">count</th>
                            <th className="py-1 pr-2 text-right">amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.receivable_age_histogram.map(function (h, i) {
                            var preScope = h.checkout_year !== '(unknown)' && h.checkout_year < '2026';
                            return (
                              <tr key={i} className={'border-b border-border/30' + (preScope ? ' bg-amber-50 dark:bg-amber-950/30' : '')}>
                                <td className="py-1 pr-2 font-mono">{h.checkout_year}</td>
                                <td className="py-1 pr-2 font-mono text-right">{h.count}</td>
                                <td className="py-1 pr-2 font-mono text-right">{fmt(h.amount_native)} {h.currency}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Sample rows for the flagged mismatch */}
                {result.property_managed_booking_not_stamped_sample && result.property_managed_booking_not_stamped_sample.length > 0 && (
                  <div>
                    <h5 className="font-semibold text-xs mb-1">Sample mismatched bookings</h5>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full border-collapse">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="py-1 pr-2">platform</th>
                            <th className="py-1 pr-2">booking_id</th>
                            <th className="py-1 pr-2">property_id</th>
                            <th className="py-1 pr-2">check_in</th>
                            <th className="py-1 pr-2">check_out</th>
                            <th className="py-1 pr-2">state</th>
                            <th className="py-1 pr-2 text-right">amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.property_managed_booking_not_stamped_sample.map(function (r, i) {
                            return (
                              <tr key={i} className="border-b border-border/30">
                                <td className="py-1 pr-2 font-mono">{r.platform}</td>
                                <td className="py-1 pr-2 font-mono">{r.booking_id}</td>
                                <td className="py-1 pr-2 font-mono">{r.property_id}</td>
                                <td className="py-1 pr-2 font-mono">{(r.check_in || '').slice(0, 10)}</td>
                                <td className="py-1 pr-2 font-mono">{(r.check_out || '').slice(0, 10)}</td>
                                <td className="py-1 pr-2 font-mono">{r.payment_state}</td>
                                <td className="py-1 pr-2 font-mono text-right">{fmt(r.amount_native)} {r.currency}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {result.note && (
                  <div className="bg-muted/50 rounded p-2 text-xs text-muted-foreground">
                    {result.note}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
